import { useMemo, useState } from 'react';
import moment from 'moment';
import Select, { GroupBase, MultiValue, StylesConfig } from 'react-select';
import {
    ILeoChatColumn,
    ILeoChatFile,
    ILeoChatOption,
    ILeoChatSelectedClient,
    ILeoChatStructuredText,
} from '../../../../store/chatbox/chatboxApi';
import { IComparisonBoardClientOption } from '../../../../store/users/usersApi';
import { useTheme } from '../../../../utils/theme-context';

export interface LeoChatAgentMessage {
    _id?: string;
    agentMessageId?: string;
    topicId?: string;
    chatId?: string;
    topic?: string;
    message?: string;
    files?: ILeoChatFile[];
    options?: ILeoChatOption[];
    stage?: string;
    responseType?: string;
    summary?: string;
    text?: ILeoChatStructuredText;
    rows?: Record<string, unknown>[];
    columns?: ILeoChatColumn[];
    meta?: Record<string, unknown>;
    createdAt?: string | Date;
    details?: {
        title?: string;
        type?: string;
        [key: string]: unknown;
    };
    metadata?: {
        messageId?: string;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

export interface LeoChatUserMessage {
    message?: string;
    files?: ILeoChatFile[];
    stage?: string;
    createdAt?: string | Date;
    [key: string]: unknown;
}

export type LeoChatMessage =
    | { agent: LeoChatAgentMessage; user?: never }
    | { user: LeoChatUserMessage; agent?: never };

type ClientSelectOption = {
    value: string;
    label: string;
    client: IComparisonBoardClientOption;
};

export type LeoChatPropertyOption = {
    value: string;
    label: string;
    propertyId: string;
    propertyName: string;
    displayName: string;
    clientId: string;
    clientLabel: string;
    clientIdentifier?: string;
    type: 'property' | 'scenario';
    parentPropertyId?: string;
    parentPropertyName?: string;
    scenarioName?: string;
};

export type LeoChatPropertySaveSelection =
    | { type: 'all' }
    | { type: 'properties'; properties: LeoChatPropertyOption[] };

type PropertySelectOption =
    | {
        kind: 'all';
        value: string;
        label: string;
    }
    | {
        kind: 'property';
        value: string;
        label: string;
        property: LeoChatPropertyOption;
    };

const ALL_PROPERTIES_VALUE = '__all_properties__';

const getClientValue = (client: IComparisonBoardClientOption) =>
    client.username || client.id || client.companySub;

const getClientLabel = (client: IComparisonBoardClientOption) =>
    client.label || client.companyName || client.username || client.id;

const fileUrl = (file: ILeoChatFile) => file.url || file.link || '';

const getMessageId = (message: LeoChatAgentMessage, index: number) =>
    String(message._id || message.agentMessageId || message.metadata?.messageId || `agent-${index}`);

const optionKey = (option: ILeoChatOption) =>
    typeof option === 'string' ? undefined : option?.key;

const optionText = (option: ILeoChatOption) => {
    if (typeof option === 'string') return option;

    return String(
        option?.value
        || option?.label
        || option?.text
        || option?.message
        || option?.title
        || option?.name
        || (typeof option?.key === 'string' ? option.key : ''),
    );
};

const isClientSelectStage = (stage?: string) =>
    stage === 'clients_select' || stage === 'awaiting_client';

const isPropertySelectStage = (stage?: string) =>
    stage === 'awaiting_property' || stage === 'property_select' || stage === 'properties_select';

const LeoAvatar = () => (
    <div
        className='mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#00330f] text-[11px] font-bold text-white shadow-sm'
        aria-hidden='true'
    >
        Leo
    </div>
);

const prettifyKey = (key: string) =>
    key
        .replace(/^_+/, '')
        .replace(/([A-Z])/g, ' $1')
        .replace(/[_-]+/g, ' ')
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase());

const formatValue = (value: unknown) => {
    if (value === null || typeof value === 'undefined') return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

const getColumnKey = (column: ILeoChatColumn, index: number) => {
    if (typeof column === 'string') return column;

    return String(
        column.key
        || column.field
        || column.name
        || column.value
        || column.id
        || column.label
        || column.header
        || column.title
        || `column-${index}`,
    );
};

const getColumnLabel = (column: ILeoChatColumn, index: number) => {
    if (typeof column === 'string') return prettifyKey(column);

    return String(
        column.label
        || column.header
        || column.title
        || column.name
        || column.key
        || column.field
        || `Column ${index + 1}`,
    );
};

const createSelectStyles = <Option,>(isDark: boolean): StylesConfig<Option, true> => ({
    control: (base) => ({
        ...base,
        minHeight: 44,
        borderRadius: 8,
        borderColor: isDark ? '#3f3f46' : '#d1d5db',
        backgroundColor: isDark ? '#000000' : '#ffffff',
        boxShadow: 'none',
        '&:hover': {
            borderColor: isDark ? '#52525b' : '#9ca3af',
        },
    }),
    menu: (base) => ({
        ...base,
        zIndex: 40,
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: isDark ? '#000000' : '#ffffff',
        border: `1px solid ${isDark ? '#3f3f46' : '#e5e7eb'}`,
    }),
    option: (base, { isFocused, isSelected }) => ({
        ...base,
        backgroundColor: isSelected
            ? (isDark ? '#1f2937' : '#f3f4f6')
            : isFocused
                ? (isDark ? '#111827' : '#f9fafb')
                : 'transparent',
        color: isDark ? '#ffffff' : '#111827',
        cursor: 'pointer',
        fontSize: 14,
    }),
    multiValue: (base) => ({
        ...base,
        borderRadius: 6,
        backgroundColor: isDark ? '#1f2937' : '#eef7f1',
        maxWidth: 260,
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: isDark ? '#ffffff' : '#111827',
        fontSize: 12,
        padding: '3px 6px',
    }),
    multiValueRemove: (base) => ({
        ...base,
        borderRadius: 6,
        color: isDark ? '#d4d4d8' : '#4b5563',
        ':hover': {
            backgroundColor: isDark ? '#374151' : '#dceee3',
            color: isDark ? '#ffffff' : '#111827',
        },
    }),
    valueContainer: (base) => ({
        ...base,
        maxHeight: 92,
        overflowY: 'auto',
    }),
    input: (base) => ({
        ...base,
        color: isDark ? '#ffffff' : '#111827',
    }),
    placeholder: (base) => ({
        ...base,
        color: isDark ? '#9ca3af' : '#6b7280',
    }),
    singleValue: (base) => ({
        ...base,
        color: isDark ? '#ffffff' : '#111827',
    }),
    groupHeading: (base) => ({
        ...base,
        color: isDark ? '#a1a1aa' : '#6b7280',
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
    }),
});

const selectStagePanelClass =
    'mt-3 w-full rounded-lg border border-gray-200 bg-white p-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-borderColor-darkMode dark:bg-black';

const selectStageLabelClass =
    'mb-2 block text-xs font-semibold uppercase text-gray-500 dark:text-gray-400';

const selectStageRowClass =
    'flex flex-col gap-2 sm:flex-row sm:items-start';

const selectStageButtonClass =
    'h-11 min-w-[96px] rounded-lg border border-[#00330f] bg-[#00330f] px-4 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white dark:bg-white dark:text-black';

const MessageFiles = ({ files }: { files?: ILeoChatFile[] }) => {
    if (!files?.length) return null;

    return (
        <div className='mt-2 flex flex-wrap gap-2'>
            {files.map((file, index) => {
                const href = fileUrl(file);

                return (
                    <a
                        key={`${file.name}-${index}`}
                        href={href || undefined}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='flex max-w-[260px] items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-100 dark:border-borderColor-darkMode dark:bg-black dark:text-primary-dark dark:hover:bg-white/10'
                    >
                        <img src='/svg/icons/file-upload.svg' className='h-4 w-4 shrink-0' alt='' />
                        <span className='truncate'>{file.name}</span>
                    </a>
                );
            })}
        </div>
    );
};

const StructuredText = ({ text }: { text?: ILeoChatStructuredText }) => {
    if (!text || typeof text === 'string') return null;

    const entries = Object.entries(text).filter(([, value]) => value !== null && typeof value !== 'undefined');
    if (!entries.length) return null;

    return (
        <div className='mt-3 grid gap-2 rounded-lg border border-gray-200 bg-[#F7F8FA] p-2 dark:border-borderColor-darkMode dark:bg-black'>
            {entries.map(([key, value]) => (
                <div key={key} className='grid gap-1 text-xs sm:grid-cols-[150px_minmax(0,1fr)]'>
                    <span className='font-semibold text-gray-500 dark:text-gray-400'>
                        {prettifyKey(key)}
                    </span>
                    <span className='break-words text-[#252B37] dark:text-primary-dark'>
                        {formatValue(value)}
                    </span>
                </div>
            ))}
        </div>
    );
};

const ResponseTable = ({
    rows,
    columns,
}: {
    rows?: Record<string, unknown>[];
    columns?: ILeoChatColumn[];
}) => {
    if (!rows?.length) return null;

    const resolvedColumns = columns?.length ? columns : Object.keys(rows[0] || {});
    if (!resolvedColumns.length) return null;

    return (
        <div className='mt-3 max-h-[360px] max-w-full overflow-auto rounded-lg border border-gray-200 bg-white dark:border-borderColor-darkMode dark:bg-black'>
            <table className='min-w-full border-collapse text-left text-xs'>
                <thead className='sticky top-0 bg-gray-100 text-gray-600 dark:bg-black dark:text-gray-300'>
                    <tr>
                        {resolvedColumns.map((column, index) => (
                            <th
                                key={`${getColumnKey(column, index)}-${index}`}
                                className='whitespace-nowrap border-b border-gray-200 px-3 py-2 font-semibold dark:border-borderColor-darkMode'
                            >
                                {getColumnLabel(column, index)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className='bg-white dark:bg-[#3a3a3a]'>
                    {rows.map((row, rowIndex) => (
                        <tr
                            key={`leo-row-${rowIndex}`}
                            className='border-b border-gray-100 last:border-b-0 dark:border-borderColor-darkMode'
                        >
                            {resolvedColumns.map((column, columnIndex) => {
                                const key = getColumnKey(column, columnIndex);

                                return (
                                    <td
                                        key={`${key}-${rowIndex}-${columnIndex}`}
                                        className='max-w-[280px] px-3 py-2 align-top text-[#252B37] dark:text-primary-dark'
                                    >
                                        <span className='block break-words'>
                                            {formatValue(row[key])}
                                        </span>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const ClientSelectStage = ({
    clients,
    loadingClients,
    saving,
    disabled,
    onSave,
}: {
    clients: IComparisonBoardClientOption[];
    loadingClients?: boolean;
    saving?: boolean;
    disabled?: boolean;
    onSave: (clients: IComparisonBoardClientOption[]) => Promise<void>;
}) => {
    const { theme } = useTheme();
    const [selectedOptions, setSelectedOptions] = useState<ClientSelectOption[]>([]);

    const selectOptions = useMemo(
        () =>
            (clients || [])
                .filter((client) => getClientValue(client))
                .map((client) => ({
                    value: getClientValue(client),
                    label: getClientLabel(client),
                    client,
                })),
        [clients],
    );

    const onChange = (next: MultiValue<ClientSelectOption>) => {
        setSelectedOptions([...next]);
    };

    const handleSave = async () => {
        if (disabled || saving || selectedOptions.length === 0) return;
        await onSave(selectedOptions.map((option) => option.client));
    };

    return (
        <div className={selectStagePanelClass}>
            <label className={selectStageLabelClass}>
                Clients
            </label>
            <div className={selectStageRowClass}>
                <div className='min-w-0 flex-1'>
                    <Select
                        isMulti
                        options={selectOptions}
                        value={selectedOptions}
                        onChange={onChange}
                        isSearchable
                        isDisabled={disabled || saving || loadingClients}
                        isLoading={loadingClients}
                        styles={createSelectStyles<ClientSelectOption>(theme === 'dark')}
                        placeholder={loadingClients ? 'Loading clients...' : 'Select one or more clients'}
                        noOptionsMessage={() => loadingClients ? 'Loading clients...' : 'No clients found'}
                    />
                </div>
                <button
                    type='button'
                    disabled={disabled || saving || loadingClients || selectedOptions.length === 0}
                    onClick={handleSave}
                    className={selectStageButtonClass}
                >
                    {saving ? 'Saving...' : 'Save'}
                </button>
            </div>
        </div>
    );
};

const getPropertyOptionLabel = (property: LeoChatPropertyOption) => {
    if (property.type === 'scenario') {
        const parent = property.parentPropertyName || property.propertyName;
        const scenario = property.scenarioName || property.displayName;
        return `${parent} / ${scenario} - ${property.clientLabel}`;
    }

    return `${property.displayName} - ${property.clientLabel}`;
};

const PropertySelectStage = ({
    selectedClients,
    properties,
    loadingProperties,
    loadingError,
    saving,
    disabled,
    onSave,
}: {
    selectedClients: ILeoChatSelectedClient[];
    properties: LeoChatPropertyOption[];
    loadingProperties?: boolean;
    loadingError?: string;
    saving?: boolean;
    disabled?: boolean;
    onSave: (selection: LeoChatPropertySaveSelection) => Promise<void>;
}) => {
    const { theme } = useTheme();
    const [selectedOptions, setSelectedOptions] = useState<PropertySelectOption[]>([]);

    const allOption = useMemo<PropertySelectOption>(() => ({
        kind: 'all',
        value: ALL_PROPERTIES_VALUE,
        label: 'All properties',
    }), []);

    const groupedOptions = useMemo<GroupBase<PropertySelectOption>[]>(() => {
        const groups: GroupBase<PropertySelectOption>[] = [
            {
                label: 'All',
                options: [allOption],
            },
        ];

        const selectedClientIds = new Set(selectedClients.map((client) => client.id));
        const clientsToShow = selectedClients.length
            ? selectedClients
            : properties.reduce<ILeoChatSelectedClient[]>((acc, property) => {
                if (acc.some((client) => client.id === property.clientId)) return acc;
                acc.push({ id: property.clientId, label: property.clientLabel });
                return acc;
            }, []);

        clientsToShow.forEach((client) => {
            const clientProperties = properties
                .filter((property) =>
                    property.clientId === client.id || (!selectedClientIds.size && property.clientLabel === client.label))
                .sort((a, b) =>
                    getPropertyOptionLabel(a).localeCompare(getPropertyOptionLabel(b), undefined, { sensitivity: 'base' }))
                .map<PropertySelectOption>((property) => ({
                    kind: 'property',
                    value: property.value,
                    label: getPropertyOptionLabel(property),
                    property,
                }));

            if (clientProperties.length) {
                groups.push({
                    label: client.label,
                    options: clientProperties,
                });
            }
        });

        return groups;
    }, [allOption, properties, selectedClients]);

    const onChange = (next: MultiValue<PropertySelectOption>) => {
        const nextOptions = [...next];
        const hadAllSelected = selectedOptions.some((option) => option.kind === 'all');
        const hasAllSelected = nextOptions.some((option) => option.kind === 'all');

        if (hasAllSelected && !hadAllSelected) {
            setSelectedOptions([allOption]);
            return;
        }

        setSelectedOptions(nextOptions.filter((option) => option.kind === 'property'));
    };

    const handleSave = async () => {
        if (disabled || saving || loadingProperties || selectedOptions.length === 0) return;
        if (selectedOptions.some((option) => option.kind === 'all')) {
            await onSave({ type: 'all' });
            return;
        }

        await onSave({
            type: 'properties',
            properties: selectedOptions
                .filter((option): option is Extract<PropertySelectOption, { kind: 'property' }> => option.kind === 'property')
                .map((option) => option.property),
        });
    };

    const noSelectedClients = selectedClients.length === 0;
    const noProperties = !loadingProperties && properties.length === 0;
    const isDisabled = disabled || saving || loadingProperties || noSelectedClients || noProperties;

    return (
        <div className={selectStagePanelClass}>
            <label className={selectStageLabelClass}>
                Properties
            </label>

            <div className={selectStageRowClass}>
                <div className='min-w-0 flex-1'>
                    <Select<PropertySelectOption, true, GroupBase<PropertySelectOption>>
                        isMulti
                        options={groupedOptions}
                        value={selectedOptions}
                        onChange={onChange}
                        isSearchable
                        isDisabled={isDisabled}
                        isLoading={loadingProperties}
                        styles={createSelectStyles<PropertySelectOption>(theme === 'dark')}
                        placeholder={loadingProperties ? 'Loading properties...' : 'Select all or one or more properties'}
                        noOptionsMessage={() => loadingProperties ? 'Loading properties...' : 'No properties found'}
                        formatOptionLabel={(option, { context }) => {
                            if (context === 'value') return option.label;

                            if (option.kind === 'all') {
                                return (
                                    <div className='flex flex-col'>
                                        <span className='font-medium'>{option.label}</span>
                                        <span className='text-xs text-gray-500 dark:text-gray-400'>
                                            Across selected clients
                                        </span>
                                    </div>
                                );
                            }

                            return (
                                <div className='flex min-w-0 items-center justify-between gap-3'>
                                    <div className='min-w-0'>
                                        <div className='truncate font-medium'>{option.property.displayName}</div>
                                        <div className='truncate text-xs text-gray-500 dark:text-gray-400'>
                                            {option.property.type === 'scenario'
                                                ? `${option.property.clientLabel} - ${option.property.parentPropertyName || 'Scenario'}`
                                                : option.property.clientLabel}
                                        </div>
                                    </div>
                                    <span className='shrink-0 rounded-full bg-black/5 px-2 py-0.5 text-[11px] font-semibold text-gray-700 dark:bg-white/10 dark:text-white/80'>
                                        {option.property.type === 'scenario' ? 'Scenario' : 'Property'}
                                    </span>
                                </div>
                            );
                        }}
                    />
                </div>

                <button
                    type='button'
                    disabled={isDisabled || selectedOptions.length === 0}
                    onClick={handleSave}
                    className={selectStageButtonClass}
                >
                    {saving ? 'Saving...' : 'Save'}
                </button>
            </div>

            {(noSelectedClients || loadingError || noProperties) && (
                <p className='mt-2 text-xs text-gray-500 dark:text-gray-400'>
                    {loadingError || (noSelectedClients
                        ? 'Save one or more clients first.'
                        : 'No properties or scenarios found for the selected clients.')}
                </p>
            )}
        </div>
    );
};

const AiMessage = ({
    message,
    index,
    isLatest,
    clients,
    selectedClients,
    properties,
    loadingClients,
    loadingProperties,
    propertiesLoadError,
    savingClientSelect,
    savingPropertySelect,
    onClickOption,
    onSaveClients,
    onSaveProperties,
}: {
    message: LeoChatAgentMessage;
    index: number;
    isLatest: boolean;
    clients: IComparisonBoardClientOption[];
    selectedClients: ILeoChatSelectedClient[];
    properties: LeoChatPropertyOption[];
    loadingClients?: boolean;
    loadingProperties?: boolean;
    propertiesLoadError?: string;
    savingClientSelect?: boolean;
    savingPropertySelect?: boolean;
    onClickOption: (option: ILeoChatOption) => void;
    onSaveClients: (clients: IComparisonBoardClientOption[]) => Promise<void>;
    onSaveProperties: (selection: LeoChatPropertySaveSelection) => Promise<void>;
}) => {
    const [copied, setCopied] = useState(false);
    const [speaking, setSpeaking] = useState(false);
    const messageId = getMessageId(message, index);
    const hasSelectStage = isClientSelectStage(message.stage) || isPropertySelectStage(message.stage);

    const handleCopy = async () => {
        await navigator.clipboard?.writeText(message.message || '');
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const handleSpeak = () => {
        if (!window.speechSynthesis || !message.message) return;
        if (speaking) {
            window.speechSynthesis.cancel();
            setSpeaking(false);
            return;
        }

        const utterance = new SpeechSynthesisUtterance(message.message);
        utterance.lang = 'en-US';
        utterance.onend = () => setSpeaking(false);
        window.speechSynthesis.speak(utterance);
        setSpeaking(true);
    };

    return (
        <div className='flex items-start gap-3 py-2'>
            <LeoAvatar />

            <div
                className={`max-w-[calc(100%_-_44px)] rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm leading-6 text-[#252B37] shadow-sm dark:border-borderColor-darkMode dark:bg-black dark:text-primary-dark ${
                    hasSelectStage ? 'w-full md:max-w-[860px]' : 'md:max-w-[78%]'
                }`}
            >
                {!!message.message && (
                    <div className='whitespace-pre-wrap break-words'>
                        {message.message}
                    </div>
                )}

                <StructuredText text={message.text} />
                <ResponseTable rows={message.rows} columns={message.columns} />

                <MessageFiles files={message.files} />

                {isClientSelectStage(message.stage) && (
                    <ClientSelectStage
                        clients={clients}
                        loadingClients={loadingClients}
                        saving={savingClientSelect}
                        disabled={!isLatest}
                        onSave={onSaveClients}
                    />
                )}

                {isPropertySelectStage(message.stage) && (
                    <PropertySelectStage
                        selectedClients={selectedClients}
                        properties={properties}
                        loadingProperties={loadingProperties}
                        loadingError={propertiesLoadError}
                        saving={savingPropertySelect}
                        disabled={!isLatest}
                        onSave={onSaveProperties}
                    />
                )}

                {!!message.options?.length && (
                    <div className='mt-3 flex flex-col items-start gap-2'>
                        {message.options.map((option, optionIndex) => (
                            <button
                                key={`${messageId}-option-${optionIndex}`}
                                type='button'
                                onClick={() => onClickOption(option)}
                                disabled={!isLatest}
                                className='inline-flex max-w-full items-center rounded-lg border border-[#00330f] bg-white px-3 py-1.5 text-left text-sm text-[#00330f] hover:bg-[#F3F7F4] disabled:cursor-not-allowed disabled:opacity-60 dark:border-white dark:bg-black dark:text-white dark:hover:bg-white/10'
                            >
                                {optionKey(option) ? (
                                    <span className='mr-1 font-semibold'>{optionKey(option)}.</span>
                                ) : (
                                    <span className='mr-2 h-1.5 w-1.5 rounded-full bg-[#00330f] dark:bg-white' />
                                )}
                                <span className='truncate'>{optionText(option)}</span>
                            </button>
                        ))}
                    </div>
                )}

                <div className='mt-2 flex items-center justify-between gap-3'>
                    <span className='text-xs text-gray-400'>
                        {message.createdAt ? moment(message.createdAt).format('hh:mm A') : ''}
                    </span>
                    <div className='flex items-center gap-1'>
                        <button
                            type='button'
                            onClick={handleSpeak}
                            className='rounded-full p-1 hover:bg-gray-100 dark:hover:bg-white/10'
                            aria-label={speaking ? 'Stop audio' : 'Play audio'}
                        >
                            <img src={speaking ? '/svg/icons/active/audio-active.svg' : '/svg/icons/audio.svg'} className='h-4 w-4' alt='' />
                        </button>
                        <button
                            type='button'
                            onClick={handleCopy}
                            className='rounded-full p-1 hover:bg-gray-100 dark:hover:bg-white/10'
                            aria-label='Copy message'
                        >
                            <img src={copied ? '/svg/icons/active/copy-active.svg' : '/svg/icons/copy.svg'} className='h-4 w-4' alt='' />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const UserMessage = ({ message }: { message: LeoChatUserMessage }) => (
    <div className='flex justify-end py-2'>
        <div className='max-w-[92%] rounded-lg bg-[#00330f] px-3 py-2 text-sm leading-6 text-white shadow-sm dark:bg-white dark:text-black md:max-w-[72%]'>
            {!!message.message && (
                <div className='whitespace-pre-wrap break-words'>{message.message}</div>
            )}
            <MessageFiles files={message.files} />
            <div className='mt-2 text-right text-xs text-white/65 dark:text-black/50'>
                {message.createdAt ? moment(message.createdAt).format('hh:mm A') : ''}
            </div>
        </div>
    </div>
);

const LeoChatMessages = ({
    messages,
    clients,
    selectedClients,
    properties,
    loadingClients,
    loadingProperties,
    propertiesLoadError,
    savingClientSelect,
    savingPropertySelect,
    onClickOption,
    onSaveClients,
    onSaveProperties,
}: {
    messages: LeoChatMessage[];
    clients: IComparisonBoardClientOption[];
    selectedClients: ILeoChatSelectedClient[];
    properties: LeoChatPropertyOption[];
    loadingClients?: boolean;
    loadingProperties?: boolean;
    propertiesLoadError?: string;
    savingClientSelect?: boolean;
    savingPropertySelect?: boolean;
    onClickOption: (option: ILeoChatOption) => void;
    onSaveClients: (clients: IComparisonBoardClientOption[]) => Promise<void>;
    onSaveProperties: (selection: LeoChatPropertySaveSelection) => Promise<void>;
}) => {
    const latestAgentIndex = useMemo(() => {
        for (let index = messages.length - 1; index >= 0; index -= 1) {
            if (messages[index]?.agent) return index;
        }
        return -1;
    }, [messages]);

    return (
        <>
            {messages.map((message, index) => {
                if (message.agent) {
                    return (
                        <AiMessage
                            key={getMessageId(message.agent, index)}
                            message={message.agent}
                            index={index}
                            isLatest={index === latestAgentIndex}
                            clients={clients}
                            selectedClients={selectedClients}
                            properties={properties}
                            loadingClients={loadingClients}
                            loadingProperties={loadingProperties}
                            propertiesLoadError={propertiesLoadError}
                            savingClientSelect={savingClientSelect}
                            savingPropertySelect={savingPropertySelect}
                            onClickOption={onClickOption}
                            onSaveClients={onSaveClients}
                            onSaveProperties={onSaveProperties}
                        />
                    );
                }

                return (
                    <UserMessage
                        key={`user-${index}-${message.user?.createdAt || ''}`}
                        message={message.user}
                    />
                );
            })}
        </>
    );
};

export default LeoChatMessages;
